/**
 * Authentication Validation Schemas
 *
 * Reusable Zod validation schemas for auth forms
 * Used with vee-validate via @/lib/zodTypedSchema
 */

import * as z from 'zod'

/**
 * Email validation schema
 * Checks for valid email format and required field
 */
export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Invalid email address')
  .max(255, 'Email is too long')

/**
 * Password validation schema
 * Requires minimum 6 characters to match backend requirements
 */
export const passwordSchema = z
  .string()
  .min(1, 'Password is required')
  .min(6, 'Password must be at least 6 characters')
  .max(128, 'Password is too long')

/**
 * Email-only validation schema
 * Used for the first step of the multi-step email auth flow
 */
export const emailOnlySchema = z.object({
  email: emailSchema,
})

/**
 * Sign-in form validation schema
 * Email and password fields
 */
export const signInSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
})

/**
 * Password reset form validation schema
 * Email only
 */
export const resetPasswordSchema = z.object({
  email: emailSchema,
})

/**
 * Phone number validation schema (DEPRECATED - manual validation used instead)
 * This schema is kept for backward compatibility but is not used in phone auth flow
 * Phone validation is now done manually in PhoneAuthForm using libphonenumber-js
 */
export const phoneNumberSchema = z
  .string()
  .min(1, 'Phone number is required')

/**
 * Verification code validation schema
 * 6-digit numeric code
 */
export const verificationCodeSchema = z
  .string()
  .min(1, 'Verification code is required')
  .regex(/^\d{6}$/, 'Code must be 6 digits')

/**
 * Phone verification form validation schema (step 2)
 * Verification code input only - phone validation is manual
 */
export const phoneVerificationSchema = z.object({
  code: verificationCodeSchema,
})

/**
 * Safely extract redirect path from route query.
 * Handles string | string[] | null | undefined and
 * only allows internal paths (starting with /) to prevent open redirects.
 */
export function getSafeRedirectPath(value: string | (string | null)[] | null | undefined): string | null {
  const path = Array.isArray(value) ? value[0] : value
  if (typeof path === 'string' && path.startsWith('/')) {
    return path
  }
  return null
}

/**
 * Export form data types for TypeScript
 */
export type EmailOnlyFormData = z.infer<typeof emailOnlySchema>
export type SignInFormData = z.infer<typeof signInSchema>
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>
export type PhoneVerificationFormData = z.infer<typeof phoneVerificationSchema>
